"use client";

import type * as React from "react";
import { useMemo } from "react";
import Image from "next/image";
import { format, parseISO } from "date-fns";
import { useSearchParams } from "next/navigation";
import {
  buildInclusionItems,
  getIncludedOverviewLabels,
} from "@/lib/booking/package-inclusions";
import { parseBookingSearchParams } from "@/lib/booking/search-params";
import { formatTimeLabel } from "@/lib/booking/time-slots";
import { useBookingLocations } from "@/lib/hooks/use-booking-locations";
import { useAppSelector } from "@/lib/redux/hooks";
import {
  selectUserSelectedMiscellaneous,
  selectUserSelectedPackage,
  selectUserSelectedVehicle,
} from "@/lib/redux/features/user-selected-details/userSelectedDetailsSlice";
import {
  formatAud,
  getSimilarModelLabel,
  getVehicleDisplayName,
  sanitizeImageUrl,
} from "@/lib/vehicles";
import { LucideCheck, LucideInfo, LucideStore } from "lucide-react";

function formatBookingDateTime(date: string, timeMinutes?: number): string {
  const formattedDate = format(parseISO(date), "EEE, MMM dd, yyyy");
  if (timeMinutes === undefined) {
    return formattedDate;
  }

  return `${formattedDate} | ${formatTimeLabel(timeMinutes)}`;
}

type PickupReturnStopProps = {
  label: string;
  location: React.ReactNode;
  dateTime: string;
};

const PickupReturnStop: React.FC<PickupReturnStopProps> = ({
  label,
  location,
  dateTime,
}) => (
  <div>
    <p className="text-sm text-gray-500">{label}</p>
    <p className="mt-0.5 text-base font-bold text-gray-900">{location}</p>
    <p className="mt-0.5 text-sm text-gray-600">{dateTime}</p>
  </div>
);

export const BookingConfirmSidebar: React.FC = () => {
  const searchParams = useSearchParams();
  const booking = parseBookingSearchParams(searchParams);
  const { getLocationLabel, isLoading: isLocationsLoading } = useBookingLocations();

  const vehicle = useAppSelector(selectUserSelectedVehicle);
  const selectedPackage = useAppSelector(selectUserSelectedPackage);
  const miscellaneous = useAppSelector(selectUserSelectedMiscellaneous);

  const pickupLabel = booking ? getLocationLabel(booking.pickup) : "";
  const returnLabel = booking
    ? getLocationLabel(booking.return ?? booking.pickup)
    : "";

  const vehicleTitle = vehicle ? getVehicleDisplayName(vehicle) : null;
  const similarLabel = vehicle ? getSimilarModelLabel(vehicle) : null;
  const vehicleCategory = vehicle?.type_name?.trim() || null;
  const imageUrl = vehicle ? sanitizeImageUrl(vehicle.similar_car_image) : "";

  const includedItems = useMemo(() => {
    const items: string[] = [];

    if (selectedPackage) {
      const inclusionItems = buildInclusionItems([], [selectedPackage]);
      items.push(
        ...getIncludedOverviewLabels(selectedPackage, inclusionItems),
      );
    }

    miscellaneous?.addOns.forEach((item) => {
      const quantity = miscellaneous.quantities[item.key] ?? 0;
      if (quantity <= 0) {
        return;
      }

      if (quantity === 1 && item.maximum_packs === 1) {
        items.push(item.name);
        return;
      }

      items.push(`${quantity} x ${item.name}`);
    });

    return items;
  }, [selectedPackage, miscellaneous]);

  if (!vehicle && !booking) {
    return (
      <aside className="w-full shrink-0 xl:w-96">
        <div className="rounded-xl border border-gray-200 bg-slate-100 px-6 py-8 text-center text-sm text-gray-500">
          No booking details available. Please complete the previous steps.
        </div>
      </aside>
    );
  }

  return (
    <aside className="w-full shrink-0 xl:w-96">
      <div className="rounded-xl border border-gray-200 bg-slate-100">
        {vehicle ? (
          <div className="flex items-center gap-4 border-b border-gray-200 px-5 py-4">
            <div className="relative size-20 shrink-0 overflow-hidden rounded-lg bg-white">
              {imageUrl ? (
                <Image
                  src={imageUrl}
                  alt={similarLabel ?? vehicleTitle ?? "Selected vehicle"}
                  fill
                  className="object-contain p-1"
                  unoptimized
                />
              ) : null}
            </div>
            <div className="min-w-0">
              <h3 className="text-base font-bold text-gray-900">
                {vehicleTitle}
              </h3>
              <p className="mt-0.5 text-sm text-gray-600">
                {similarLabel && similarLabel !== vehicleTitle
                  ? `${similarLabel} or similar`
                  : "or similar"}
                {vehicleCategory ? ` | ${vehicleCategory}` : ""}
              </p>
            </div>
          </div>
        ) : null}

        {booking ? (
          <div className="border-b border-gray-200 px-5 py-4">
            <h4 className="text-base font-bold text-gray-900">
              Pickup and return
            </h4>

            <div className="mt-4 flex gap-3">
              <div className="flex flex-col items-center pt-1">
                <LucideStore
                  size={22}
                  strokeWidth={1.5}
                  className="shrink-0 text-gray-800"
                  aria-hidden="true"
                />
                <div className="my-2 w-px flex-1 bg-gray-300" aria-hidden="true" />
                <LucideStore
                  size={22}
                  strokeWidth={1.5}
                  className="shrink-0 text-gray-800"
                  aria-hidden="true"
                />
              </div>

              <div className="flex min-w-0 flex-1 flex-col justify-between gap-8 pb-1">
                <PickupReturnStop
                  label="Pickup"
                  location={
                    isLocationsLoading && !pickupLabel ? (
                      <span
                        className="inline-block h-5 w-40 animate-pulse rounded bg-gray-200"
                        aria-hidden="true"
                      />
                    ) : (
                      pickupLabel || booking.pickup
                    )
                  }
                  dateTime={formatBookingDateTime(
                    booking.pickupDate,
                    booking.pickupTime,
                  )}
                />

                <PickupReturnStop
                  label="Return"
                  location={
                    isLocationsLoading && !returnLabel ? (
                      <span
                        className="inline-block h-5 w-40 animate-pulse rounded bg-gray-200"
                        aria-hidden="true"
                      />
                    ) : (
                      returnLabel || booking.return || booking.pickup
                    )
                  }
                  dateTime={formatBookingDateTime(
                    booking.returnDate,
                    booking.returnTime,
                  )}
                />
              </div>
            </div>
          </div>
        ) : null}

        <div className="border-b border-gray-200 px-5 py-4">
          <p className="text-sm font-semibold text-gray-900">Booking option</p>
          <div className="mt-2 flex items-center gap-3">
            <span className="inline-flex shrink-0 items-center justify-center rounded bg-green-700 px-2.5 py-1.5 text-[10px] font-bold uppercase leading-none tracking-wide whitespace-nowrap text-white">
              Best price
            </span>
            <span className="text-sm leading-snug text-gray-700">
              Lowest price available for your rental
            </span>
          </div>
        </div>

        <div className="px-5 py-4">
          <h4 className="text-sm font-semibold text-gray-900">
            What&apos;s included
          </h4>
          {selectedPackage ? (
            <p className="mt-1 text-sm font-medium text-gray-700">
              {selectedPackage.name}
              {selectedPackage.price_per_day === null
                ? " — Included"
                : ` — AU$ ${formatAud(selectedPackage.price_per_day)} / day`}
            </p>
          ) : null}

          {includedItems.length > 0 ? (
            <ul className="mt-3 space-y-2.5">
              {includedItems.map((label) => (
                <li
                  key={label}
                  className="flex items-start justify-between gap-2 text-sm text-gray-700"
                >
                  <span className="flex items-start gap-2">
                    <LucideCheck
                      size={14}
                      className="mt-0.5 shrink-0 text-green-600"
                    />
                    <span>{label}</span>
                  </span>
                  <LucideInfo
                    size={14}
                    className="mt-0.5 shrink-0 text-gray-400"
                  />
                </li>
              ))}
            </ul>
          ) : (
            <p className="mt-2 text-sm text-gray-500">
              No package or add-on details selected.
            </p>
          )}

          <p className="mt-4 text-xs text-gray-500">
            Unlimited kilometers
          </p>
        </div>
      </div>
    </aside>
  );
};
